import React, { useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Box } from '@mui/material';

// TinyMCE is self-hosted rather than loaded from the cloud: the cloud build refuses
// to run on a domain that is not registered against an API key, which is how it
// broke on cms.liveolympiad.org. Bundling it removes both the key and the domain
// approval from the picture.
import 'tinymce/tinymce';
import 'tinymce/models/dom/model';
import 'tinymce/themes/silver';
import 'tinymce/icons/default';
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/help';
import 'tinymce/plugins/wordcount';

// The skin ships as CSS. The two content sheets are pulled in as strings and handed
// to content_style, since content_css is disabled for the self-hosted build.
import 'tinymce/skins/ui/oxide/skin.min.css';
import contentCss from 'tinymce/skins/content/default/content.min.css?inline';
import contentUiCss from 'tinymce/skins/ui/oxide/content.min.css?inline';

import MathEditor, { MATH_CLASS, buildMathHtml } from '../MathEditor';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: number;
  disabled?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  height = 300,
  disabled = false,
}) => {
  const editorRef = useRef<any>(null);
  const [mathOpen, setMathOpen] = React.useState(false);
  const [mathInitial, setMathInitial] = React.useState('');

  const handleEditorChange = (content: string) => {
    onChange(content);
  };

  /** Opens the equation dialog, seeded from the equation under the cursor if there is one. */
  const openMathEditor = () => {
    const node = editorRef.current?.selection?.getNode();
    const existing = node?.closest?.(`span.${MATH_CLASS}`);
    setMathInitial(existing?.getAttribute('data-latex') || '');
    setMathOpen(true);
  };

  const insertMath = (latex: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    const node = editor.selection.getNode();
    const existing = node?.closest?.(`span.${MATH_CLASS}`);
    if (existing) {
      // Replace in place rather than nesting a new equation inside the old one.
      editor.selection.select(existing);
    }
    editor.insertContent(buildMathHtml(latex));
  };

  return (
    <Box className="rich-text-editor">
      <Editor
        onInit={(evt, editor) => editorRef.current = editor}
        value={value}
        onEditorChange={handleEditorChange}
        disabled={disabled}
        init={{
          height,
          menubar: false,
          skin: false,
          content_css: false,
          // Without these TinyMCE strips the MathML and the data-latex we need to re-edit.
          extended_valid_elements:
            'span[class|data-latex|contenteditable],math[*],semantics[*],annotation[*],' +
            'mrow[*],mi[*],mn[*],mo[*],msup[*],msub[*],msubsup[*],mfrac[*],msqrt[*],mroot[*],' +
            'mtext[*],mspace[*],mover[*],munder[*],munderover[*],mtable[*],mtr[*],mtd[*],mstyle[*],mpadded[*],mphantom[*],menclose[*]',
          custom_elements: 'math',
          setup: (editor: any) => {
            editor.ui.registry.addButton('lomath', {
              text: '\u221Ax',
              tooltip: 'Insert equation',
              onAction: openMathEditor,
            });
            // Double-clicking an equation reopens it for editing.
            editor.on('dblclick', (event: any) => {
              if (event.target?.closest?.(`span.${MATH_CLASS}`)) {
                openMathEditor();
              }
            });
          },
          plugins: [
            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
            'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount',
          ],
          toolbar:
            'undo redo | blocks | ' +
            'bold italic underline strikethrough | alignleft aligncenter ' +
            'alignright alignjustify | bullist numlist outdent indent | ' +
            'removeformat | image link | lomath | code preview | help',
          content_style: [contentCss, contentUiCss, `
            body { 
              font-family: 'Inter', 'Roboto', 'Helvetica', 'Arial', sans-serif; 
              font-size: 16px; 
              line-height: 1.6;
              color: #1e232c;
            }
            img {
              max-width: 100%;
              height: auto;
            }
          `].join('\n'),
          placeholder,
          branding: false,
          resize: false,
          statusbar: false,
          
          // Image upload configuration
          images_upload_handler: async (blobInfo: any, success: any, failure: any) => {
            try {
              // TODO: Implement image upload to your CDN
              const formData = new FormData();
              formData.append('file', blobInfo.blob(), blobInfo.filename());
              
              // This would be your actual upload endpoint
              const response = await fetch('/api/upload/image', {
                method: 'POST',
                body: formData,
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
              });
              
              if (response.ok) {
                const result = await response.json();
                success(result.url);
              } else {
                failure('Image upload failed');
              }
            } catch (error) {
              failure('Image upload failed');
            }
          },
          
          // Custom formats for educational content
          formats: {
            highlight: { inline: 'mark', styles: { backgroundColor: '#ffeb3b' } },
            code: { inline: 'code', styles: { backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' } },
          },
          
          // Additional configuration for educational content
          extended_valid_elements: 'math[*],mrow[*],mi[*],mo[*],mn[*],msup[*],msub[*],mfrac[*]',
          
          setup: (editor: any) => {
            // Custom button for highlighting important text
            editor.ui.registry.addButton('highlight', {
              text: 'Highlight',
              onAction: () => {
                editor.formatter.toggle('highlight');
              },
            });
            
            // Custom button for inline code
            editor.ui.registry.addButton('inlinecode', {
              text: 'Code',
              onAction: () => {
                editor.formatter.toggle('code');
              },
            });
          },
        }}
      />
      <MathEditor
        open={mathOpen}
        initialLatex={mathInitial}
        onClose={() => setMathOpen(false)}
        onInsert={insertMath}
      />
    </Box>
  );
};

export default RichTextEditor;
